import { useTranslation } from "@next-platform/client-common-core";
import { CapabilityGate, SubCapabilityKey } from "@next-platform/client-common-core/infra/application/capabilities";
import { FilterV2Panel } from "@next-platform/client-common-spicy-ui";
import React from "react";

import { FilterWrapper } from "@/components/search/FilterWrapper";

import type { FilterPanelCharacterProps } from "../FilterBox/types";
import { DisplayContext } from "../types";
import { SEARCH_FILTERS_PANEL_ID } from "./constants";
import FilterSectionV2 from "./FilterSectionV2";

const FilterPanelCharacterV2 = ({
  context,
  isDesktop,
  isOpen,
  onOpenChange,
  setFeaturesSlot,
  hasFilterDefinition,
  hasTagRefinements,
  definitionRefinementCount,
  tagsListData,
  tagsActionHandler,
  definitionListData,
  definitionActionHandler,
  languageRefinementCount,
  languageListData,
  languageActionHandler,
  chatSizeRefinementCount,
  chatSizeListData,
  chatSizeActionHandler,
}: FilterPanelCharacterProps) => {
  const { t } = useTranslation();
  const tagsTitle = t("chatbotFilters:filterByTag.title");
  const tagSearchPlaceholder = t("chatbotFilters:filterByTag.placeholder");
  const definitionSizeTitle = t("chatbotFilters:filterByDefinitionSize.title");
  const languageTitle = t("chatbotFilters:filterByLanguage.title");
  const languageSearchPlaceholder = t("chatbotFilters:filterByLanguage.placeholder");
  const chatSizeTitle = t("chatbotFilters:filterByChatSize.title");

  return (
    <FilterWrapper desktopWidth={260} isDesktop={isDesktop} onOpenChange={onOpenChange} open={isOpen}>
      <FilterV2Panel className="w-full max-h-full" data-testid="FilterPanelCharacterV2" id={SEARCH_FILTERS_PANEL_ID}>
        <FilterSectionV2
          actionHandler={tagsActionHandler}
          className="flex shrink flex-col data-[state=open]:min-h-[240px]"
          bodyClassName="min-h-0 flex-1"
          data-testid="TagsCapabilityGate"
          defaultOpen
          hasExclude
          hasActiveRefinements={hasTagRefinements}
          items={tagsListData}
          searchPlaceholder={tagSearchPlaceholder}
          title={tagsTitle}
        />
        {context !== DisplayContext.MEMBER_SELECT ? <div className="contents" data-testid="FeaturesFilterSlot" ref={setFeaturesSlot} /> : null}
        {hasFilterDefinition ? (
          <CapabilityGate capability={SubCapabilityKey.DiscoverFilterDefinitionSize}>
            <FilterSectionV2
              actionHandler={definitionActionHandler}
              data-testid="DefinitionSizeCapabilityGate"
              defaultOpen={false}
              hasActiveRefinements={definitionRefinementCount > 0}
              items={definitionListData}
              title={definitionSizeTitle}
            />
          </CapabilityGate>
        ) : null}
        <CapabilityGate capability={SubCapabilityKey.DiscoverFilterLanguage}>
          <FilterSectionV2
            actionHandler={languageActionHandler}
            data-testid="LanguageCapabilityGate"
            defaultOpen={false}
            hasActiveRefinements={languageRefinementCount > 0}
            items={languageListData}
            searchPlaceholder={languageSearchPlaceholder}
            title={languageTitle}
          />
        </CapabilityGate>
        {context !== DisplayContext.MEMBER_SELECT ? (
          <CapabilityGate capability={SubCapabilityKey.DiscoverFilterGroups}>
            <FilterSectionV2
              actionHandler={chatSizeActionHandler}
              data-testid="GroupSizeCapabilityGate"
              defaultOpen={false}
              hasActiveRefinements={chatSizeRefinementCount > 0}
              items={chatSizeListData}
              title={chatSizeTitle}
            />
          </CapabilityGate>
        ) : null}
      </FilterV2Panel>
    </FilterWrapper>
  );
};

export default React.memo(FilterPanelCharacterV2);
